import { useQueryClient } from "@tanstack/react-query";
import type { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Watch } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import {
  SubscriptionInfoBox,
  SubscriptionInfoRow,
} from "@/components/Layout/SubscriptionInfoBox/SubscriptionInfoBox";
import { AutoRenewalBillPreview } from "@/components/Portal/DeviceManagement/components/AutoRenewalBillPreview";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import { useSubscriptionPriceBreakdown } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/useSubscriptionPriceBreakdown";
import { useAlert } from "@/hooks/useAlert";
import { useSetAutoRenew } from "@/services/api";
import { AlertTypes } from "@/utils/atoms";
import { hasSimAutoRenewal, isWatchSimPausing } from "@/utils/deviceUtils";
import { getNextBillDate } from "@/utils/tariffUtils";
import { capitalizeFirstLetter } from "@/utils/translationHelpers";

interface ChangeWatchSimRenewalProps {
  /**
   * Rendered inline (desktop) instead of as a full page (mobile) - mirrors
   * `SubscriptionAutoRenewal`. Omitted when mounted as the route's own
   * element (the mobile case).
   */
  onClose?: () => void;
}

export const ChangeWatchSimRenewal: FC<ChangeWatchSimRenewalProps> = ({
  onClose,
}) => {
  const { t: tPrefixed } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.watchSim.autoRenewal",
  });
  const { t, i18n } = useTranslation(["portal", "translation"]);
  const { t: tDefault } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showAlert = useAlert();

  const { data: simDetail, isPending: isLoading } = useDisplayedSim();

  const close = () =>
    onClose
      ? onClose()
      : navigate(`../display?simId=${simDetail?.simId}`, { replace: true });

  // Same source of truth as the status CTA — flagged cancellation, or an
  // ORDERED Watch whose successor has already dropped it.
  const isEnableScreen = !!simDetail && isWatchSimPausing(simDetail);
  const willEnableMainSimAutoRenewal =
    isEnableScreen && !!simDetail && !hasSimAutoRenewal(simDetail);

  const copy = isEnableScreen
    ? {
        title: tPrefixed("enable.title"),
        successMessage: tDefault("portal:common.autoRenewal.successMessage"),
        goodToKnow: willEnableMainSimAutoRenewal
          ? tPrefixed("enable.goodToKnowText.withMainSimAR")
          : tDefault("portal:common.autoRenewal.goodToKnow"),
      }
    : {
        title: tDefault("portal:cockpit.managementTile.watchSim.status.pause"),
        successMessage: tPrefixed("disable.successMessage"),
        goodToKnow: tPrefixed("disable.goodToKnowText"),
      };

  const { mutate: sendSetAutoRenew, isPending: isLoadingSetAutoRenew } =
    useSetAutoRenew({
      mutation: {
        onSuccess: () =>
          // Same "stay pending until the panel actually reflects the new
          // state" ordering as SubscriptionAutoRenewal.
          queryClient
            .invalidateQueries({ queryKey: ["/sim/sim"] })
            .finally(() => {
              close();
              showAlert({
                type: AlertTypes.success,
                text: copy.successMessage,
              });
            }),
      },
    });

  const toggleWatchSimAR = () => {
    if (!simDetail) {
      return;
    }
    sendSetAutoRenew({
      simId: willEnableMainSimAutoRenewal
        ? simDetail.simId!
        : simDetail.watchSim!.watchId!,
      params: { autoRenew: isEnableScreen },
    });
  };

  const nextBillDate = getNextBillDate(simDetail, i18n.language);
  // Watch SIM is already listed as an add-on by this hook, so the enable
  // screen's breakdown falls out of it for free - see SubscriptionAutoRenewal.
  const priceBreakdown = useSubscriptionPriceBreakdown(
    isEnableScreen ? simDetail : undefined,
  );

  if (isLoading || !simDetail) {
    // Rendered inline (desktop) alongside the rest of the tab - a full-page
    // takeover would cover unrelated cards that are already visible. The
    // route-mounted (mobile, no onClose) case still gets the full screen.
    return onClose ? (
      <CardModule dataTestid="change-watch-sim-renewal" title="" bodyGap={2}>
        <LoadingSpinner />
      </CardModule>
    ) : (
      <BrandLoadingScreen />
    );
  }

  return (
    <CardModule
      dataTestid="change-watch-sim-renewal"
      stickyHeader
      title={copy.title}
      actions={
        <>
          <Button
            dataTestid="cancel-button"
            size="sm"
            variant="secondary"
            onClick={close}
          >
            {capitalizeFirstLetter(t("common.buttons.cancel"))}
          </Button>
          <Button
            dataTestid="success-button"
            size="sm"
            variant="primary"
            disabled={isLoadingSetAutoRenew}
            onClick={toggleWatchSimAR}
          >
            {capitalizeFirstLetter(t("common.buttons.confirm"))}
          </Button>
        </>
      }
      bodyGap={2}
    >
      {isEnableScreen && (
        <p className="text-body-base font-semibold text-foreground-primary">
          {copy.title}
        </p>
      )}

      <p className="text-body-sm text-foreground-secondary">
        {isEnableScreen ? (
          <Trans
            t={tPrefixed}
            values={{ count: simDetail.minContractDuration }}
          >
            enable.description
          </Trans>
        ) : (
          <Trans t={tPrefixed} values={{ count: simDetail.remainingDays }}>
            disable.description
          </Trans>
        )}
      </p>

      <SubscriptionInfoBox>
        <SubscriptionInfoRow
          icon={<Watch className="size-6" />}
          title={t("translation:Common.products.watchSim")}
        />
      </SubscriptionInfoBox>

      {isEnableScreen && (
        <AutoRenewalBillPreview
          title={tDefault("portal:common.autoRenewal.nextBillTitle")}
          description={tPrefixed("enable.nextBillDescription")}
          breakdown={priceBreakdown}
          minContractDuration={simDetail.minContractDuration}
          nextBillDate={nextBillDate}
        />
      )}

      <Notification
        title={tDefault("Content.goodToKnow")}
        description={copy.goodToKnow}
      />
    </CardModule>
  );
};
